import { useEffect } from 'react'
import { InterviewStatus } from '@bosmart/types'
import { handleError, MediaError } from '@/lib/errors'
import type { SessionRefs } from './types'

interface MediaInitializationProps {
  refs: SessionRefs
  setStatus: (status: InterviewStatus) => void
  setError: (message: string) => void
}

export function useMediaInitialization({ refs, setStatus, setError }: MediaInitializationProps) {
  useEffect(() => {
    const initMedia = async () => {
      try {
        refs.mediaStreamRef.current?.getTracks().forEach((track) => track.stop())

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: { width: 1280, height: 720 },
        })

        if (refs.videoRef.current) {
          refs.videoRef.current.srcObject = stream
        }
        refs.mediaStreamRef.current = stream
        setStatus(InterviewStatus.CONNECTED)
      } catch (err: unknown) {
        const mediaError = getMediaError(err)
        const result = handleError(mediaError, 'InterviewSession:initMedia')
        setError(result.userMessage)
      }
    }

    void initMedia()
  }, [refs.mediaStreamRef, refs.videoRef, setError, setStatus])
}

function getMediaError(err: unknown): MediaError {
  if (err instanceof Error) {
    if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
      return new MediaError(
        'Camera/Microphone access denied. Please allow permissions in your browser settings to proceed.',
        'MEDIA_PERMISSION_DENIED'
      )
    }
    if (err.name === 'NotFoundError') {
      return new MediaError(
        'No camera or microphone found. Please connect a device and reload.',
        'MEDIA_NOT_FOUND'
      )
    }
    if (err.name === 'NotReadableError') {
      return new MediaError(
        'Camera or microphone is currently in use by another application. Please close other apps and retry.',
        'MEDIA_IN_USE'
      )
    }
  }

  return new MediaError(
    'Failed to access camera/microphone. Please allow permissions.',
    'MEDIA_ACCESS_FAILED'
  )
}
